import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Grid, Row, Column } from '@lux/components';

import { BodySecondary, H3, SubtitleSecondary } from '../Base';
import Modal from '../Modal';
import Spacing from '../Spacing';

const StyledBanner = styled.div`
  position: relative;
  height: 204px;
  ${p => p.theme.media.md`
  height: 320px;
  `};

  * {
    ${p =>
      p.bannerTheme === 'dark' &&
      css`
        color: ${p => p.theme.colours.white};

        ${StyledButton} {
          color: ${p => p.theme.colours.grey_900};
          background-color: ${p => p.theme.colours.white};
        }
      `};

    ${p =>
      p.bannerTheme === 'light' &&
      css`
        color: ${p => p.theme.colours.grey_900};

        ${StyledButton} {
          color: ${p => p.theme.colours.white};
          background-color: ${p => p.theme.colours.primary};
        }
      `};
  }
`;

const StyledGrid = styled(props => <Grid {...props} />)`
  height: 100%;
  position: relative;
`;

const StyledRow = styled(props => <Row {...props} />)`
  height: 100%;
`;

const StyledImageWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  object-fit: cover;

  > * {
    height: 100%;
  }
`;

const StyledPreviewImage = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  height: 32px;
  font-size: 14px;
  padding: 0 ${p => p.theme.spacing(2)};
  ${p => p.theme.media.md`
  height: 48px;
  font-size: 16px;
  padding: 0 ${p => p.theme.spacing(3)};
  `};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
`;

const PromoBanner = props => {
  const {
    bannerTheme,
    desktopImg,
    mobileImg,
    title,
    description,
    enableCta,
    ctaText,
    modalTitle,
    modalDescription,
    previewMode,
    desktopFileReference,
    mobileFileReference
  } = props;

  const [modalOpen, setModalOpen] = useState(false);

  return (
    <Fragment>
      <StyledBanner
        bannerTheme={bannerTheme}
        title={title}
        description={description}
      >
        <Row>
          <Column sm={12} md={false}>
            <StyledImageWrapper>
              {!previewMode && mobileImg}
              {previewMode && <StyledPreviewImage src={mobileFileReference} />}
            </StyledImageWrapper>
          </Column>
          <Column sm={false} md={12}>
            <StyledImageWrapper>
              {!previewMode && desktopImg}
              {previewMode && <StyledPreviewImage src={desktopFileReference} />}
            </StyledImageWrapper>
          </Column>
        </Row>
        <StyledGrid>
          <StyledRow middle="sm">
            <Column sm={7} md={6}>
              <Spacing bottom={1} bottomMd={4}>
                <Spacing bottom={0.5} bottomMd={3}>
                  <H3>{title}</H3>
                </Spacing>
                <SubtitleSecondary>{description}</SubtitleSecondary>
              </Spacing>
              {enableCta && (
                <StyledButton onClick={() => setModalOpen(true)}>
                  {ctaText}
                </StyledButton>
              )}
            </Column>
          </StyledRow>
        </StyledGrid>
      </StyledBanner>
      <Modal
        open={modalOpen}
        title={modalTitle}
        onClose={() => setModalOpen(false)}
      >
        <BodySecondary dangerouslySetInnerHTML={{ __html: modalDescription }} />
      </Modal>
    </Fragment>
  );
};

PromoBanner.defaultProps = {
  bannerTheme: 'dark'
};

PromoBanner.propTypes = {
  /**
   * Banner theme
   */
  bannerTheme: PropTypes.oneOf(['dark', 'light']),
  /**
   * Image source for desktop view
   */
  desktopImg: PropTypes.element,
  /**
   * Image source for mobile view
   */
  mobileImg: PropTypes.element,
  /**
   * Title of the banner
   */
  title: PropTypes.string,
  /**
   * Description of the banner
   */
  description: PropTypes.string,
  /**
   * Show or hide the CTA button
   */
  enableCta: PropTypes.bool,
  /**
   * CTA Text
   */
  ctaText: PropTypes.string,
  /**
   * Title in the modal
   */
  modalTitle: PropTypes.string,
  /**
   * Description in the modal
   */
  modalDescription: PropTypes.string,
  desktopFileReference: PropTypes.string,
  mobileFileReference: PropTypes.string
};

export default PromoBanner;
